import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu as MenuIcon, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import Logo from '@/assets/logo.svg?react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Header: React.FC = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const appBaseUrl = import.meta.env.VITE_APP_URL || '';

  const navLinks = [
    { href: '/features', label: 'How it Works' },
    { href: '/pricing', label: 'Pricing' },
  ];

  const handleMobileLinkClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const appLoginUrl = appBaseUrl ? `${appBaseUrl}/auth` : '/auth';
  const appSignUpUrl = appBaseUrl ? `${appBaseUrl}/auth?mode=signup` : '/auth?mode=signup';
  const isHome = location.pathname === '/';

  return (
    <header className="sticky top-0 z-50 w-full px-3 pt-3 md:px-4">
      {isHome && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(circle_at_18%_100%,rgba(16,185,129,0.12),transparent_40%),radial-gradient(circle_at_78%_10%,rgba(212,175,55,0.12),transparent_42%),linear-gradient(180deg,#020617_0%,#081331_100%)]"
        />
      )}
      <div className="relative mx-auto flex w-full max-w-6xl items-center rounded-[1.1rem] border border-slate-200 bg-white px-3 py-3 shadow-[0_10px_30px_-22px_rgba(15,23,42,0.35)] md:px-4">
        <Link to="/" className="mr-auto flex items-center gap-2.5 rounded-xl px-1.5 py-1 text-slate-900 transition-colors hover:bg-slate-50">
          <span className="grid h-9 w-9 place-content-center rounded-xl border border-slate-200 bg-slate-950 shadow-sm">
            <Logo className="h-5 w-5" />
          </span>
          <span className="font-sans text-[1.55rem] font-semibold tracking-[-0.03em] text-slate-900">LendLifted</span>
        </Link>
        {!isMobile && (
          <nav className="ml-4 hidden items-center rounded-full border border-slate-200 bg-slate-100 px-1.5 py-1 text-[0.9rem] font-medium tracking-[-0.01em] text-slate-700 md:flex">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="rounded-full px-4 py-2 transition hover:bg-white hover:text-slate-900"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}
        <div className={`ml-8 flex items-center gap-2 sm:gap-3 ${isMobile ? 'ml-auto' : ''}`}>
          {!isMobile && (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="rounded-full border border-slate-200 bg-white px-4 text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:ring-slate-300"
              >
                <a href={appLoginUrl}>Login</a>
              </Button>
              <Button
                asChild
                size="sm"
                className="rounded-full bg-slate-900 px-5 text-white shadow-[0_8px_14px_-10px_rgba(15,23,42,0.65)] hover:bg-slate-800 focus-visible:ring-slate-400"
              >
                <a href={appSignUpUrl}>Start Selling</a>
              </Button>
            </>
          )}
          {isMobile && (
            <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open menu"
                  className="h-10 w-10 rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900"
                >
                  {mobileMenuOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="mt-2 w-64 rounded-2xl border-slate-200 bg-white p-2 shadow-[0_20px_40px_-22px_rgba(15,23,42,0.35)]"
              >
                {navLinks.map(link => (
                  <DropdownMenuItem
                    key={link.href}
                    onClick={() => handleMobileLinkClick(link.href)}
                    className="rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:bg-slate-100 focus:text-slate-900"
                  >
                    {link.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="my-2 bg-slate-200" />
                <DropdownMenuItem
                  onClick={() => { window.location.href = appLoginUrl; }}
                  className="rounded-xl px-4 py-3 text-sm font-medium text-slate-700 focus:bg-slate-100 focus:text-slate-900"
                >
                  Login
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { window.location.href = appSignUpUrl; }}
                  className="mt-1 rounded-xl border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-semibold text-white focus:bg-slate-800 focus:text-white"
                >
                  Start Selling
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
